import type { ApiError, AIAgent } from './types'
import { generateTraceparent, shouldInjectTraceparent } from '../lib/tracing'

const BASE_URL = import.meta.env.VITE_API_URL || ''

/**
 * ApiClient provides a typed wrapper around fetch with:
 * - Automatic JSON parsing
 * - Error handling and typed ApiError throwing
 * - Request/response logging in development
 */
class ApiClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${BASE_URL}${endpoint}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    }
    // REG-14: propagate a fresh trace-context header so one browser action
    // continues as a single trace into the backend (otelgin already extracts
    // and parents under an incoming traceparent — see src/lib/tracing.ts).
    if (shouldInjectTraceparent(url)) {
      headers.traceparent = generateTraceparent()
    }

    const config: RequestInit = {
      ...options,
      credentials: 'include',
      headers,
    }

    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`[API] ${options.method || 'GET'} ${endpoint}`)
    }

    try {
      const response = await fetch(url, config)

      // Handle 401 — redirect to login page unless already there.
      // Preserve the current path as ?next= so the user lands back after login.
      if (response.status === 401) {
        const onAuthPage = ['/login', '/logout'].includes(window.location.pathname)
        if (!onAuthPage) {
          const next = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/login?next=${next}`
          return new Promise(() => {}) // never settles; browser is navigating away
        }
        // Already on login page — fall through to throw the error normally
      }

      // REG-37: the org's trial ended and TenantContext is refusing the
      // entire API. Handled once here rather than in each caller, so no
      // page has to know the lock exists. Deliberately distinct from 402,
      // which this app already uses for "this feature needs a Pro licence"
      // and which is a different screen entirely. Checked on status alone,
      // before the body is parsed: the gate is the only thing in the app
      // that returns 423, so there is no code to disambiguate. The guard
      // mirrors the 401 and MFA paths above -- without it, the pay wall's
      // own failing requests would bounce it against itself.
      if (response.status === 423) {
        if (window.location.pathname !== '/trial-expired') {
          window.location.href = '/trial-expired'
          return new Promise(() => {}) // never settles; browser is navigating away
        }
      }

      // Some successful endpoints intentionally return no representation.
      // Treat every empty success body consistently, rather than assuming
      // that only a 204 response can be bodyless.
      const contentLength = response.headers?.get?.('content-length')
      if (response.status === 204 || contentLength === '0') {
        if (import.meta.env.DEV) {
          console.log(`[API] ${options.method || 'GET'} ${endpoint} → ${response.status} No Content`)
        }
        return undefined as T
      }

      // Parse JSON response. Browser Responses expose text(), while the
      // lightweight response doubles used by existing callers may only
      // implement json(). Reading text first lets a successful empty body
      // resolve cleanly instead of throwing "Unexpected end of JSON input".
      let data: unknown
      if (typeof response.text === 'function') {
        const body = await response.text()
        try {
          data = body ? JSON.parse(body) : undefined
        } catch {
          // A proxy in front of the app (Cloudflare) can answer with its own
          // HTML page. Say what happened instead of "Unexpected token '<'".
          throw new Error(
            response.ok
              ? 'The server sent a response this page could not read. Please try again.'
              : `The server could not complete the request (error ${response.status}). Please try again in a moment.`,
          )
        }
      } else {
        data = await response.json()
      }

      // REG-175: the org requires MFA and this user has not enrolled. A
      // distinct code rather than the generic 403 so this lands on the
      // enrolment page instead of a dead end. The guard mirrors the 401
      // path above: without it, the enrolment page's own failing requests
      // would bounce it against itself.
      if (response.status === 403 && (data as ApiError)?.error?.code === 'mfa_enrollment_required') {
        if (window.location.pathname !== '/mfa-required') {
          window.location.href = '/mfa-required'
          return new Promise(() => {}) // never settles; browser is navigating away
        }
      }

      // Handle non-2xx responses
      if (!response.ok) {
        const error = data as ApiError
        const message = error?.error?.message || 'Request failed'
        const apiError = new Error(message) as Error & { response: ApiError }
        apiError.response = error
        throw apiError
      }

      if (import.meta.env.DEV) {
        console.log(`[API] ${options.method || 'GET'} ${endpoint} → ${response.status}`)
      }

      return data as T
    } catch (error) {
      if (error instanceof Error && 'response' in error) {
        // Already an API error with response attached
        throw error
      }

      // Network error or other exception
      if (error instanceof Error) {
        throw error
      }

      throw new Error('Network request failed')
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number | undefined> | object): Promise<T> {
    let query = ''
    if (params) {
      const filteredParams = Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])

      if (filteredParams.length > 0) {
        query = '?' + new URLSearchParams(filteredParams).toString()
      }
    }

    return this.request<T>(endpoint + query, {
      method: 'GET',
    })
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    })
  }

  // delete() takes no body. Some DELETE endpoints need one (e.g. disabling
  // MFA requires the current password as a step-up check), so this carries
  // it as JSON rather than smuggling it into a query string.
  async deleteWithBody<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export const apiClient = new ApiClient()

export async function listAgents(): Promise<AIAgent[]> {
  return apiClient.get<AIAgent[]>('/api/v1/agents')
}

export async function setAgentStatus(id: string, active: boolean): Promise<AIAgent> {
  return apiClient.patch<AIAgent>(`/api/v1/agents/${id}/status`, { active })
}
